import ReactGA from 'react-ga4';

// This property owns Velocity's existing BigQuery export. A build without an
// environment file must never fall back to another brand's Analytics property.
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-0QY1W93TZR';
let activeMeasurementId = MEASUREMENT_ID;

function sendLocatorEvent(name, parameters) {
  // The locator is embedded beside other Google tags. Do not broadcast its
  // events to every configured destination on the host page.
  ReactGA.event(name, { ...parameters, send_to: activeMeasurementId });
}

// Setup analytics - consolidated from analyticsInit.js
export function setupAnalytics() {
  if (!MEASUREMENT_ID) {
    console.warn('GA4 Measurement ID not found.');
    return false;
  }

  const initialized = initializeGA(MEASUREMENT_ID);
  
  if (!import.meta.env.PROD) {
    console.log(`ReactGA4 initialization ${initialized ? 'successful' : 'failed'} with ID: ${MEASUREMENT_ID}`);
  }
  
  return initialized;
}

// Function to initialize GA4
export function initializeGA(measurementId) {
  if (!measurementId) {
    if (!import.meta.env.PROD) {
      console.warn('⚠️ GA4 measurement ID is not provided - analytics not initialized');
    }
    return false;
  }
  
  // Skip GA initialization in development
  if (!import.meta.env.PROD) {
    console.log('🚫 Development mode detected - GA4 analytics disabled (logging only)');
    console.log('📊 Would initialize GA4 with measurement ID:', measurementId);
    return true; // Return true so analytics functions still log
  }
  
  activeMeasurementId = measurementId;
  // Initialize ReactGA4 only in production
  ReactGA.initialize(measurementId, {
    gaOptions: {
      debug_mode: false
    },
    gtagOptions: {
      send_page_view: false // index.jsx sends the initial page view once
    }
  });
  
  // console.log('✅ GA4 initialized with measurement ID:', measurementId);
  return true;
}

// Function to track page views
export function trackPageView(path) {
  if (!import.meta.env.PROD) {
    console.log('📊 GA4 Pageview (dev mode - not sent):', path);
    return;
  }
  
  ReactGA.send({ hitType: 'pageview', page: path, send_to: activeMeasurementId });
  // console.log('📊 GA4 Pageview:', path);
}

// Function to track custom events
export function trackEvent(category, action, label, value) {
  if (!import.meta.env.PROD) {
    console.log('📊 GA4 Event (dev mode - not sent):', { category, action, label, value });
    return;
  }
  
  if (!category || !action) return;
  const redactEmail = (text) => typeof text === 'string' && text.includes('@')
    ? 'REDACTED (Potential Email Address)' : text;
  sendLocatorEvent(redactEmail(action), {
    event_category: redactEmail(category),
    event_label: redactEmail(label),
    value
  });
  
  // console.log('📊 GA4 Event:', { category, action, label, value });
}

// Enhanced ecommerce methods

// Product view event
export function trackProductViewGA4(product, store, canonicalProduct) {
  // Get canonical product info if available
  const productName = canonicalProduct?.canonicalName || product.name;
  const productBrand = canonicalProduct?.brand || 'Good Feels';
  const productCategory = canonicalProduct?.category || product.category || '';
  const price = product.configs?.[0]?.normalPrice || 0;
  
  if (!import.meta.env.PROD) {
    console.log('📊 GA4 Product View (dev mode - not sent):', { 
      product: productName, 
      canonical: canonicalProduct?.canonicalName, 
      store: store.fields.Name 
    });
    return;
  }
  
  // ReactGA.event('view_item', {
  //   currency: 'USD',
  //   value: price,
  //   items: [{
  //     item_id: product.id,
  //     item_name: productName,
  //     price: price,
  //     discount: product.configs?.[0]?.salePrice ? (product.configs[0].normalPrice - product.configs[0].salePrice) : 0,
  //     item_brand: productBrand,
  //     item_category: productCategory,
  //     quantity: 1,
  //     affiliation: store.fields.Name,
  //     location_id: store.fields.LitAlertsId
  //   }]
  // });
  
  // Also track a custom event for more detailed analytics
  sendLocatorEvent('product_view', {
    product_name: productName,
    product_id: product.id,
    canonical_name: canonicalProduct?.canonicalName || '',
    store_name: store.fields.Name,
    store_id: store.fields.LitAlertsId,
    price: product.configs?.[0]?.salePrice ? (product.configs[0].normalPrice - product.configs[0].salePrice) : price,
    discount: product.configs?.[0]?.salePrice ? (price - (product.configs[0].normalPrice - product.configs[0].salePrice)) : 0,
    product_brand: productBrand,
    product_category: productCategory,
    quantity: 1,
    // affiliation: store.fields.Name,
    // location_id: store.fields.LitAlertsId
  });
  
  // console.log('📊 GA4 Product View:', { 
  //   product: productName, 
  //   canonical: canonicalProduct?.canonicalName, 
  //   store: store.fields.Name 
  // });
}

// Store detail view
export function trackStoreOpenGA4(store) {
  if (!import.meta.env.PROD) {
    console.log('📊 GA4 Store Open (dev mode - not sent):', { store: store.fields.Name });
    return;
  }
  
  // ReactGA.event('select_content', {
  //   content_type: 'store',
  //   content_id: store.fields.LitAlertsId,
  //   items: [{
  //     item_id: store.fields.LitAlertsId,
  //     item_name: store.fields.Name,
  //     item_category: 'Store',
  //     item_variant: store.fields.State,
  //     item_brand: 'Good Feels'
  //   }]
  // });
  
  // Also track custom event
  const eventData = {
    store_name: store.fields.Name,
    store_id: store.fields.LitAlertsId,
    store_city: store.fields.City,
    store_state: store.fields.State,
    store_address: `${store.fields.City}, ${store.fields.State}`
  };
  
  // If this was triggered by a product click, include the product name
  if (store.fields._triggerProduct) {
    eventData.product_name = store.fields._triggerProduct.canonicalName || store.fields._triggerProduct.name;
    eventData.trigger_product_id = store.fields._triggerProduct.id;
  }
  
  sendLocatorEvent('store_modal_open', eventData);
  
  // console.log('📊 GA4 Store Open:', { store: store.fields.Name });
}

// Track store website visit
export function trackStoreWebsiteVisitGA4(store, type) {
  if (!import.meta.env.PROD) {
    console.log('📊 GA4 Store Website Visit (dev mode - not sent):', { store: store.fields.Name, type });
    return;
  }
  
  sendLocatorEvent('store_website_visit', {
    store_name: store.fields.Name,
    store_id: store.fields.LitAlertsId,
    store_city: store.fields.City,
    store_state: store.fields.State,
    visit_type: type
  });
  
  // console.log('📊 GA4 Store Website Visit:', { store: store.fields.Name, type });
}

// Track get directions
export function trackGetDirectionsGA4(store) {
  if (!import.meta.env.PROD) {
    console.log('📊 GA4 Get Directions (dev mode - not sent):', { store: store.fields.Name });
    return;
  }
  
  sendLocatorEvent('get_directions', {
    store_name: store.fields.Name,
    store_city: store.fields.City,
    store_state: store.fields.State
  });
  
  // console.log('📊 GA4 Get Directions:', { store: store.fields.Name });
}

// Track product search - simplified to remove source distinction
export function trackProductSearchGA4(searchParams) {
  if (!searchParams || (!searchParams.canonicalNames && !searchParams.searchTerm)) {
    return false;
  }
  
  if (!import.meta.env.PROD) {
    console.log('📊 GA4 Product Search (dev mode - not sent):', {
      search_term: searchParams.searchTerm,
      canonical_names: searchParams.canonicalNames
    });
    return true;
  }
  
  // Track all product selections without source parameter
  sendLocatorEvent('search', {
    search_term: searchParams.searchTerm || 'canonical-product-search',
    canonical_names: Array.isArray(searchParams.canonicalNames) ? searchParams.canonicalNames.join(',') : ''
  });
  
  // console.log('📊 GA4 Product Search:', {
  //   search_term: searchParams.searchTerm,
  //   canonical_names: searchParams.canonicalNames
  // });
  return true;
}

